import React from 'react';
import { Link } from 'react-router-dom';
import { Play, Square } from 'lucide-react';
import { time as timeApi } from '@/lib/api';
import { toast } from 'sonner';

// Persistent floating timer badge — polls /api/time/running every 30s and
// ticks the display every second while a timer is live. Visible on every
// authenticated staff page via AppLayout's header.
export default function TimerWidget() {
  const [running, setRunning] = React.useState(null);
  const [, setTick] = React.useState(0);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(() => {
    timeApi.running().then(setRunning).catch(() => setRunning(null));
  }, []);

  React.useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  React.useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [running]);

  React.useEffect(() => {
    const onFocus = () => load();
    const onCustom = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('immipro:timer-changed', onCustom);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('immipro:timer-changed', onCustom);
    };
  }, [load]);

  const elapsed = () => {
    if (!running?.started_at) return '0:00';
    const sec = Math.max(0, Math.floor((Date.now() - new Date(running.started_at).getTime()) / 1000));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
  };

  const stop = async () => {
    if (!running || busy) return;
    setBusy(true);
    try {
      await timeApi.stop(running.id);
      toast.success('Timer stopped');
      setRunning(null);
      window.dispatchEvent(new CustomEvent('immipro:timer-changed'));
    } catch {
      toast.error('Failed to stop timer');
    } finally { setBusy(false); }
  };

  if (!running) {
    return (
      <Link
        to="/app/time"
        data-testid="header-timer-start"
        className="hidden items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-500 transition hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-700 md:flex"
        title="Start a new time entry"
      >
        <Play className="h-3.5 w-3.5" /> Start timer
      </Link>
    );
  }

  return (
    <div
      data-testid="header-timer-running"
      className="hidden items-center gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-800 md:flex"
      title={running.description}
    >
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
      <Link to="/app/time" className="max-w-[180px] truncate font-semibold hover:underline" data-testid="header-timer-desc">
        {running.description}
      </Link>
      <span className="font-mono tabular-nums" data-testid="header-timer-elapsed">{elapsed()}</span>
      <button
        onClick={stop}
        disabled={busy}
        data-testid="header-timer-stop"
        className="rounded p-0.5 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
        title="Stop timer"
      >
        <Square className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
